export const COGO_UNIVER_FORMULA_ORACLE_SCHEMA_VERSION =
  "cogo.sheet-formula-oracle.v1" as const;

export const COGO_UNIVER_FORMULA_ORACLE_RUN_SCHEMA_VERSION =
  "cogo.sheet-formula-oracle-run.v1" as const;

export const UNIVER_FORMULA_ENGINE_PACKAGE = "@univerjs/engine-formula";
export const UNIVER_SHEETS_FORMULA_PACKAGE = "@univerjs/sheets-formula";

export type CogoFormulaOracleStatus = "ready" | "warn" | "blocked" | "missing";

export type CogoFormulaCellValue = string | number | boolean | null;

export interface CogoFormulaCellInput {
  address: string;
  value: CogoFormulaCellValue;
  label?: string;
}

export type CogoFormulaOracleCaseSource =
  | "project-evidence"
  | "cogo-default"
  | "xlsx-extraction"
  | "actionflow-checkpoint"
  | "manual-fixture";

export interface CogoFormulaActionFlowCheckpoint {
  flowId: string;
  stepId: string;
  resultPath: string;
  compareMode?: "equals" | "approx" | "truthy" | "custom";
}

export interface CogoFormulaActionFlowBinding extends CogoFormulaActionFlowCheckpoint {
  id: string;
  caseId: string;
}

export interface CogoFormulaCalculationGraphSummary {
  schemaVersion: "cogo.sheet-calculation-graph.v1";
  formulaCaseCount: number;
  inputCellCount: number;
  namedRefCount: number;
  chartCount: number;
  chartSeriesCount: number;
  actionFlowStepCount: number;
  nodeCount: number;
  edgeCount: number;
  evidenceRef: string;
}

export interface CogoFormulaOraclePosition {
  sheet: string;
  row: number;
  col: number;
}

export interface CogoUniverFormulaOracleCase {
  id: string;
  label: string;
  formula: string;
  sourceFormula?: string;
  expectedValue: CogoFormulaCellValue;
  actualValue?: CogoFormulaCellValue;
  pass?: boolean;
  inputs: CogoFormulaCellInput[];
  dependencies: string[];
  position?: CogoFormulaOraclePosition;
  namedRefs?: Record<string, unknown>;
  structuredRefs?: Record<string, unknown>[];
  chartSeriesRefs?: Record<string, unknown>[];
  actionFlowFormulaMapping?: Record<string, unknown>;
  source: CogoFormulaOracleCaseSource;
  actionFlowCheckpoint?: CogoFormulaActionFlowCheckpoint;
}

export interface CogoUniverFormulaOracleModel {
  schemaVersion: typeof COGO_UNIVER_FORMULA_ORACLE_SCHEMA_VERSION;
  status: CogoFormulaOracleStatus;
  engine: "univer";
  enginePackage: typeof UNIVER_FORMULA_ENGINE_PACKAGE;
  sheetsFormulaPackage: typeof UNIVER_SHEETS_FORMULA_PACKAGE;
  integrationMode: "lazy-plugin-headless";
  corePolicy: "upstream-unmodified";
  caseCount: number;
  passCount: number;
  dependencyCount: number;
  cases: CogoUniverFormulaOracleCase[];
  actionFlowBindings?: CogoFormulaActionFlowBinding[];
  calculationGraph?: CogoFormulaCalculationGraphSummary;
  blockers: string[];
  evidenceRef: string;
}

export interface CogoUniverFormulaOracleRunCase extends CogoUniverFormulaOracleCase {
  actualValue: CogoFormulaCellValue;
  pass: boolean;
  error?: string;
}

export interface CogoUniverFormulaOracleRun {
  schemaVersion: typeof COGO_UNIVER_FORMULA_ORACLE_RUN_SCHEMA_VERSION;
  status: CogoFormulaOracleStatus;
  engine: "univer";
  enginePackage: typeof UNIVER_FORMULA_ENGINE_PACKAGE;
  sheetsFormulaPackage: typeof UNIVER_SHEETS_FORMULA_PACKAGE;
  integrationMode: "lazy-plugin-headless";
  executedAt: string;
  caseCount: number;
  passCount: number;
  cases: CogoUniverFormulaOracleRunCase[];
  blockers: string[];
}

/**
 * Ids of the three built-in sample cases this oracle runs when a project
 * carries no formula evidence of its own. They are LABEL KEYS, not copy: the
 * report surface resolves them through its locale table at the render sink,
 * so the sample a Korean operator sees is named in Korean. Exported so that
 * table and the report-workspace i18n gate both derive from this declaration
 * instead of restating it.
 */
export const UNIVER_FORMULA_DEFAULT_CASE_IDS = [
  "report-total-sum",
  "report-change-rate",
  "actionflow-checkpoint",
] as const;

export type UniverFormulaDefaultCaseId =
  (typeof UNIVER_FORMULA_DEFAULT_CASE_IDS)[number];

const DEFAULT_FORMULA_CASES: CogoUniverFormulaOracleCase[] = [
  {
    id: "report-total-sum",
    label: "report-total-sum",
    formula: "=SUM(A1:A3)",
    expectedValue: 500,
    inputs: [
      { address: "A1", value: 100, label: "prior" },
      { address: "A2", value: 150, label: "current" },
      { address: "A3", value: 250, label: "forecast" },
    ],
    dependencies: ["A1", "A2", "A3"],
    source: "cogo-default",
  },
  {
    id: "report-change-rate",
    label: "report-change-rate",
    formula: "=ROUND((A2-A1)/A1,4)",
    expectedValue: 0.25,
    inputs: [
      { address: "A1", value: 800, label: "base" },
      { address: "A2", value: 1000, label: "latest" },
    ],
    dependencies: ["A1", "A2"],
    source: "cogo-default",
  },
  {
    id: "actionflow-checkpoint",
    label: "actionflow-checkpoint",
    formula: "=A1+A2",
    expectedValue: 42,
    inputs: [
      { address: "A1", value: 41, label: "actionflow-result" },
      { address: "A2", value: 1, label: "delta" },
    ],
    dependencies: ["A1", "A2"],
    source: "actionflow-checkpoint",
    actionFlowCheckpoint: {
      flowId: "report-formula-oracle",
      stepId: "actionflow-checkpoint",
      resultPath: "_results.actionflowCheckpoint",
      compareMode: "equals",
    },
  },
];

function readRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function readArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function readBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readScalar(value: unknown): CogoFormulaCellValue | undefined {
  if (value === null) return null;
  if (typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return undefined;
}

function readDeep(source: unknown, path: string[]): unknown {
  let current: unknown = source;
  for (const key of path) {
    const record = readRecord(current);
    if (!record) return undefined;
    current = record[key];
  }
  return current;
}

function normalizeStatus(value: unknown): CogoFormulaOracleStatus | null {
  const text = readString(value)?.toLowerCase();
  if (
    text === "ready" ||
    text === "warn" ||
    text === "blocked" ||
    text === "missing"
  ) {
    return text;
  }
  if (text === "pass" || text === "passed") return "ready";
  if (text === "fail" || text === "failed") return "blocked";
  return null;
}

function readStringList(value: unknown): string[] {
  return readArray(value).flatMap((entry) => {
    const text = readString(entry);
    return text ? [text] : [];
  });
}

function readRecordArray(value: unknown): Record<string, unknown>[] {
  return readArray(value).flatMap((entry) => {
    const record = readRecord(entry);
    return record ? [record] : [];
  });
}

function readPosition(value: unknown): CogoFormulaOraclePosition | undefined {
  const record = readRecord(value);
  if (
    typeof record?.sheet !== "string" ||
    typeof record.row !== "number" ||
    typeof record.col !== "number"
  ) {
    return undefined;
  }
  return { sheet: record.sheet, row: record.row, col: record.col };
}

function normalizeCaseSource(value: unknown): CogoFormulaOracleCaseSource {
  const text = readString(value);
  if (
    text === "project-evidence" ||
    text === "cogo-default" ||
    text === "xlsx-extraction" ||
    text === "actionflow-checkpoint" ||
    text === "manual-fixture"
  ) {
    return text;
  }
  return "project-evidence";
}

function readActionFlowCheckpoint(
  value: unknown,
): CogoFormulaActionFlowCheckpoint | undefined {
  const record = readRecord(value);
  const flowId = readString(record?.flowId);
  const stepId = readString(record?.stepId);
  const resultPath = readString(record?.resultPath);
  if (!flowId || !stepId || !resultPath) return undefined;
  const compareMode = readString(record?.compareMode);
  return {
    flowId,
    stepId,
    resultPath,
    ...(compareMode === "equals" ||
    compareMode === "approx" ||
    compareMode === "truthy" ||
    compareMode === "custom"
      ? { compareMode }
      : {}),
  };
}

function readCellInputs(value: unknown): CogoFormulaCellInput[] {
  return readArray(value).flatMap((entry) => {
    const record = readRecord(entry);
    const address = readString(record?.address) ?? readString(record?.cell);
    const scalar = readScalar(record?.value);
    if (!record || !address || scalar === undefined) return [];
    return [
      {
        address: address.toUpperCase(),
        value: scalar,
        ...(readString(record.label)
          ? { label: readString(record.label)! }
          : {}),
      },
    ];
  });
}

function normalizeOracleCase(
  entry: unknown,
  index: number,
): CogoUniverFormulaOracleCase | null {
  const record = readRecord(entry);
  if (!record) return null;
  const formula = readString(record.formula);
  if (!formula) return null;
  const id = readString(record.id) ?? `formula-case-${index + 1}`;
  const sourceFormula = readString(record.sourceFormula);
  const expectedValue =
    readScalar(record.expectedValue) ??
    readScalar(record.expected) ??
    readScalar(record.value) ??
    null;
  const actualValue =
    readScalar(record.actualValue) ?? readScalar(record.actual) ?? undefined;
  const inputs =
    readCellInputs(record.inputs).length > 0
      ? readCellInputs(record.inputs)
      : readCellInputs(record.cellInputs);
  const dependencies =
    readStringList(record.dependencies).length > 0
      ? readStringList(record.dependencies).map((value) => value.toUpperCase())
      : inputs.map((input) => input.address);
  const actionFlowCheckpoint = readActionFlowCheckpoint(
    record.actionFlowCheckpoint,
  );
  const position = readPosition(record.position);
  const namedRefs = readRecord(record.namedRefs);
  const structuredRefs = readRecordArray(record.structuredRefs);
  const chartSeriesRefs = readRecordArray(record.chartSeriesRefs);
  const actionFlowFormulaMapping = readRecord(record.actionFlowFormulaMapping);
  return {
    id,
    label: readString(record.label) ?? id,
    formula,
    ...(sourceFormula ? { sourceFormula } : {}),
    expectedValue,
    ...(actualValue !== undefined ? { actualValue } : {}),
    ...(readBoolean(record.pass) !== null
      ? { pass: readBoolean(record.pass)! }
      : {}),
    inputs,
    dependencies,
    ...(position ? { position } : {}),
    ...(namedRefs ? { namedRefs } : {}),
    ...(structuredRefs.length > 0 ? { structuredRefs } : {}),
    ...(chartSeriesRefs.length > 0 ? { chartSeriesRefs } : {}),
    ...(actionFlowFormulaMapping ? { actionFlowFormulaMapping } : {}),
    source: normalizeCaseSource(record.source),
    ...(actionFlowCheckpoint ? { actionFlowCheckpoint } : {}),
  };
}

function readFormulaOracleEvidence(project: unknown): {
  evidence: Record<string, unknown> | null;
  evidenceRef: string;
} {
  const candidates: Array<{ path: string[]; ref: string }> = [
    { path: ["univerFormulaOracle"], ref: "univerFormulaOracle" },
    { path: ["formulaOracle"], ref: "formulaOracle" },
    { path: ["sheetFormulaOracle"], ref: "sheetFormulaOracle" },
    {
      path: ["generationEvidence", "univerFormulaOracle"],
      ref: "generationEvidence.univerFormulaOracle",
    },
    {
      path: ["generationEvidence", "formulaOracle"],
      ref: "generationEvidence.formulaOracle",
    },
    {
      path: ["generationEvidence", "sheetFormulaOracle"],
      ref: "generationEvidence.sheetFormulaOracle",
    },
    {
      path: ["metadata", "univerFormulaOracle"],
      ref: "metadata.univerFormulaOracle",
    },
    { path: ["metadata", "formulaOracle"], ref: "metadata.formulaOracle" },
    {
      path: ["metadata", "sheetFormulaOracle"],
      ref: "metadata.sheetFormulaOracle",
    },
    {
      path: ["manifest", "univerFormulaOracle"],
      ref: "manifest.univerFormulaOracle",
    },
    { path: ["manifest", "formulaOracle"], ref: "manifest.formulaOracle" },
    {
      path: ["manifest", "sheetFormulaOracle"],
      ref: "manifest.sheetFormulaOracle",
    },
    {
      path: ["documentExportProductionGate", "univerFormulaOracle"],
      ref: "documentExportProductionGate.univerFormulaOracle",
    },
    {
      path: ["documentExportProductionGate", "formulaOracle"],
      ref: "documentExportProductionGate.formulaOracle",
    },
    {
      path: ["documentExportProductionGate", "sheetFormulaOracle"],
      ref: "documentExportProductionGate.sheetFormulaOracle",
    },
  ];
  for (const candidate of candidates) {
    const evidence = readRecord(readDeep(project, candidate.path));
    if (evidence) return { evidence, evidenceRef: candidate.ref };
  }
  return { evidence: null, evidenceRef: "cogo-default-report-formula-smoke" };
}

function readCalculationGraphEvidence(
  project: unknown,
  formulaEvidence: Record<string, unknown> | null,
): {
  evidence: Record<string, unknown> | null;
  evidenceRef: string;
} {
  const candidates: Array<{ value: unknown; ref: string }> = [
    {
      value: readDeep(formulaEvidence, ["calculationGraph"]),
      ref: "sheetFormulaOracle.calculationGraph",
    },
    {
      value: readDeep(formulaEvidence, ["calculationGraphSummary"]),
      ref: "sheetFormulaOracle.calculationGraphSummary",
    },
    {
      value: readDeep(project, ["sheetCalculationGraph"]),
      ref: "sheetCalculationGraph",
    },
    {
      value: readDeep(project, ["calculationGraph"]),
      ref: "calculationGraph",
    },
    {
      value: readDeep(project, ["metadata", "sheetCalculationGraph"]),
      ref: "metadata.sheetCalculationGraph",
    },
    {
      value: readDeep(project, ["metadata", "calculationGraph"]),
      ref: "metadata.calculationGraph",
    },
    {
      value: readDeep(project, ["generationEvidence", "sheetCalculationGraph"]),
      ref: "generationEvidence.sheetCalculationGraph",
    },
    {
      value: readDeep(project, [
        "documentExportProductionGate",
        "sheetCalculationGraph",
      ]),
      ref: "documentExportProductionGate.sheetCalculationGraph",
    },
  ];
  for (const candidate of candidates) {
    const record = readRecord(candidate.value);
    if (record) return { evidence: record, evidenceRef: candidate.ref };
  }
  return { evidence: null, evidenceRef: "" };
}

function normalizeCalculationGraphSummary(
  project: unknown,
  formulaEvidence: Record<string, unknown> | null,
): CogoFormulaCalculationGraphSummary | undefined {
  const { evidence, evidenceRef } = readCalculationGraphEvidence(
    project,
    formulaEvidence,
  );
  const summary = readRecord(evidence?.summary) ?? evidence;
  const formulaCaseCount = readNumber(summary?.formulaCaseCount);
  const chartSeriesCount = readNumber(summary?.chartSeriesCount);
  const actionFlowStepCount = readNumber(summary?.actionFlowStepCount);
  const nodeCount = readNumber(summary?.nodeCount);
  const edgeCount = readNumber(summary?.edgeCount);
  if (
    formulaCaseCount === null &&
    chartSeriesCount === null &&
    actionFlowStepCount === null &&
    nodeCount === null &&
    edgeCount === null
  ) {
    return undefined;
  }
  return {
    schemaVersion: "cogo.sheet-calculation-graph.v1",
    formulaCaseCount: formulaCaseCount ?? 0,
    inputCellCount: readNumber(summary?.inputCellCount) ?? 0,
    namedRefCount: readNumber(summary?.namedRefCount) ?? 0,
    chartCount: readNumber(summary?.chartCount) ?? 0,
    chartSeriesCount: chartSeriesCount ?? 0,
    actionFlowStepCount: actionFlowStepCount ?? 0,
    nodeCount: nodeCount ?? 0,
    edgeCount: edgeCount ?? 0,
    evidenceRef,
  };
}

function valuesEquivalent(
  expected: CogoFormulaCellValue | undefined,
  actual: CogoFormulaCellValue | undefined,
): boolean {
  if (expected === undefined || actual === undefined) return false;
  if (typeof expected === "number" && typeof actual === "number") {
    return Math.abs(expected - actual) < 1e-9;
  }
  return expected === actual;
}

function summarizeStatus(
  cases: Array<Pick<CogoUniverFormulaOracleCase, "pass">>,
): CogoFormulaOracleStatus {
  if (cases.length === 0) return "missing";
  if (cases.every((entry) => entry.pass === true)) return "ready";
  if (cases.some((entry) => entry.pass === false)) return "blocked";
  return "warn";
}

export function buildUniverFormulaOracleModel(
  project: unknown,
): CogoUniverFormulaOracleModel {
  const { evidence, evidenceRef } = readFormulaOracleEvidence(project);
  const calculationGraph = normalizeCalculationGraphSummary(project, evidence);
  const evidenceCases = readArray(evidence?.cases).flatMap((entry, index) => {
    const normalized = normalizeOracleCase(entry, index);
    return normalized ? [normalized] : [];
  });
  const cases =
    evidenceCases.length > 0 ? evidenceCases : DEFAULT_FORMULA_CASES;
  const normalizedCases = cases.map((entry) => {
    const pass =
      entry.pass ??
      (entry.actualValue !== undefined
        ? valuesEquivalent(entry.expectedValue, entry.actualValue)
        : undefined);
    return { ...entry, pass };
  });
  const passCount = normalizedCases.filter(
    (entry) => entry.pass === true,
  ).length;
  const blockers = normalizedCases.flatMap((entry) =>
    entry.pass === false ? [`${entry.label} formula oracle did not pass.`] : [],
  );
  const actionFlowBindings = normalizedCases.flatMap((entry) =>
    entry.actionFlowCheckpoint
      ? [
          {
            id: `${entry.id}-binding`,
            caseId: entry.id,
            ...entry.actionFlowCheckpoint,
          },
        ]
      : [],
  );
  const status =
    normalizeStatus(evidence?.status) ??
    (readBoolean(evidence?.pass) === true ? "ready" : null) ??
    (evidenceCases.length === 0 ? "warn" : null) ??
    summarizeStatus(normalizedCases);

  return {
    schemaVersion: COGO_UNIVER_FORMULA_ORACLE_SCHEMA_VERSION,
    status,
    engine: "univer",
    enginePackage: UNIVER_FORMULA_ENGINE_PACKAGE,
    sheetsFormulaPackage: UNIVER_SHEETS_FORMULA_PACKAGE,
    integrationMode: "lazy-plugin-headless",
    corePolicy: "upstream-unmodified",
    caseCount: normalizedCases.length,
    passCount,
    dependencyCount: new Set(
      normalizedCases.flatMap((entry) => entry.dependencies),
    ).size,
    cases: normalizedCases,
    ...(actionFlowBindings.length > 0 ? { actionFlowBindings } : {}),
    ...(calculationGraph ? { calculationGraph } : {}),
    blockers,
    evidenceRef,
  };
}

function parseCellAddress(
  address: string,
): { row: number; col: number } | null {
  const match = /^([A-Z]+)([1-9][0-9]*)$/i.exec(address.trim());
  if (!match) return null;
  const [, letters, rowText] = match;
  let col = 0;
  for (const letter of letters.toUpperCase()) {
    col = col * 26 + (letter.charCodeAt(0) - 64);
  }
  return { row: Number(rowText) - 1, col: col - 1 };
}

function addCellValue(
  cellData: Record<number, Record<number, { v: CogoFormulaCellValue }>>,
  input: CogoFormulaCellInput,
): string | null {
  const address = parseCellAddress(input.address);
  if (!address) return `Invalid cell address: ${input.address}`;
  cellData[address.row] ??= {};
  cellData[address.row]![address.col] = { v: input.value };
  return null;
}

function resultValueAt(
  result: unknown,
  unitId: string,
  sheetId: string,
): CogoFormulaCellValue | undefined {
  const unit = readRecord(result)?.[unitId];
  const sheet = readRecord(unit)?.[sheetId];
  const row = readRecord(sheet)?.["999"];
  const cell = readRecord(row)?.["0"];
  const first = Array.isArray(cell) ? readRecord(cell[0]) : null;
  return readScalar(first?.value);
}

function sanitizeSheetId(id: string, index: number): string {
  const normalized = id.toLowerCase().replace(/[^a-z0-9_-]+/g, "-");
  return `${normalized || "formula-case"}-${index + 1}`;
}

export async function runUniverFormulaOracle(
  cases: CogoUniverFormulaOracleCase[],
  options: { timeoutMs?: number } = {},
): Promise<CogoUniverFormulaOracleRun> {
  const [core, facade, formulaEngine, sheets, sheetsFormula] =
    await Promise.all([
      import("@univerjs/core"),
      import("@univerjs/core/facade"),
      import("@univerjs/engine-formula"),
      import("@univerjs/sheets"),
      import("@univerjs/sheets-formula"),
      import("@univerjs/engine-formula/facade"),
      import("@univerjs/sheets/facade"),
      import("@univerjs/sheets-formula/facade"),
    ]);

  const unitId = "cogo-formula-oracle";
  const sheetOrder = cases.map((entry, index) =>
    sanitizeSheetId(entry.id, index),
  );
  const sheetsById: Record<string, unknown> = {};
  const formulaMap: Record<
    string,
    Record<string, Record<number, Record<number, string[]>>>
  > = { [unitId]: {} };
  const setupErrors = new Map<string, string[]>();

  cases.forEach((entry, index) => {
    const sheetId = sheetOrder[index]!;
    const cellData: Record<
      number,
      Record<number, { v: CogoFormulaCellValue }>
    > = {};
    for (const input of entry.inputs) {
      const error = addCellValue(cellData, input);
      if (error) {
        setupErrors.set(sheetId, [...(setupErrors.get(sheetId) ?? []), error]);
      }
    }
    sheetsById[sheetId] = {
      id: sheetId,
      name: entry.label.slice(0, 31) || sheetId,
      cellData,
    };
    formulaMap[unitId]![sheetId] = { 999: { 0: [entry.formula] } };
  });

  const univer = new core.Univer({
    locale: core.LocaleType.EN_US,
    locales: {},
  });
  univer.registerPlugin(formulaEngine.UniverFormulaEnginePlugin);
  univer.registerPlugin(sheets.UniverSheetsPlugin);
  univer.registerPlugin(sheetsFormula.UniverSheetsFormulaPlugin);
  univer.createUnit(core.UniverInstanceType.UNIVER_SHEET, {
    id: unitId,
    name: "Coogo Formula Oracle",
    sheetOrder,
    sheets: sheetsById,
  });

  const api = facade.FUniver.newAPI(univer);
  const formulaResult = await api
    .getFormula()
    .executeFormulas(formulaMap, options.timeoutMs ?? 5000);
  const runCases = cases.map((entry, index): CogoUniverFormulaOracleRunCase => {
    const sheetId = sheetOrder[index]!;
    const actualValue = resultValueAt(formulaResult, unitId, sheetId) ?? null;
    const setupError = setupErrors.get(sheetId)?.join("; ");
    const pass = setupError
      ? false
      : valuesEquivalent(entry.expectedValue, actualValue);
    return {
      ...entry,
      actualValue,
      pass,
      ...(setupError ? { error: setupError } : {}),
    };
  });
  const passCount = runCases.filter((entry) => entry.pass).length;
  const blockers = runCases.flatMap((entry) =>
    entry.pass
      ? []
      : [
          entry.error ??
            `${entry.label} expected ${String(entry.expectedValue)} but got ${String(entry.actualValue)}.`,
        ],
  );

  return {
    schemaVersion: COGO_UNIVER_FORMULA_ORACLE_RUN_SCHEMA_VERSION,
    status: passCount === runCases.length ? "ready" : "blocked",
    engine: "univer",
    enginePackage: UNIVER_FORMULA_ENGINE_PACKAGE,
    sheetsFormulaPackage: UNIVER_SHEETS_FORMULA_PACKAGE,
    integrationMode: "lazy-plugin-headless",
    executedAt: new Date().toISOString(),
    caseCount: runCases.length,
    passCount,
    cases: runCases,
    blockers,
  };
}
